import { cn } from "@/utils/cn";
import { VideoPlayer } from "@/components/common/video-player";
import {
  MoreActionPopover,
  MoreActionItem,
} from "@/components/common/more-action-popover";
import { DeleteConfirmModal, Spinner } from "@/components/ui/modal";
import {
  DownloadIcon,
  GenerateIcon,
  MoreIcon,
  RefreshIcon,
  RenameIcon,
  TrashIcon,
  WarningRedIcon,
} from "@/components/ui/icon";
import { useTranslation } from "react-i18next";
import { TextButton } from "@/components/common/text-button";
import { RenameModal } from "@/components/common/rename-modal";
import { TruncatedText } from "@/components/common/truncated-text";
import { ChooseProjectModal } from "@/components/common/choose-project-modal";
import { formatDateSmart } from "@/utils/date-formatter";
import { Image } from "@/components/common/image";
import { IconButton } from "@/components/common/icon-button";
import unusual_cover from '@/assets/actor/unusual_cover.webp';
import { ValidMask } from "./valid-mask";

interface ActorVideoCardProps {
  className?: string;
  actor: Actor;
  name: string;
  url: string;
  poster: string;
  status: number;
  createdAt: string;
  // 是否展示apply按钮
  showApply?: boolean;
  onClick: () => void;
  onDownload: () => void;
  onRename: (name: string) => void;
  onRegenerate: () => void;
  onDelete?: () => void;
}

export const ActorVideoCard = ({
  className = "",
  actor,
  url,
  poster,
  status,
  createdAt,
  name,
  showApply,
  onClick,
  onDownload,
  onRegenerate,
  onRename,
  onDelete,
}: ActorVideoCardProps) => {
  const { t } = useTranslation();

  const isSuccess = status === 2;
  const isProcessing = status < 2;
  const isFailed = status > 2;
  const isUnusual = actor.isUnusual;
  // TODO: 待处理
  const isNotFaceDetected = status === 3 && actor.figureCode === 'InvalidFile.NotFaceDetected';

  const handleRegenerateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRegenerate) {
      onRegenerate();
    }
  };

  if (isUnusual) {
    return (
      <a href="mailto:support@bizdesk.ai" target="_blank">
        <div className="w-full h-full relative">
          <div
            className={cn(
              "group relative bg-card border border-border rounded-2xl w-full aspect-[9/16] overflow-hidden",
              "hover:shadow-md hover:border-primary/20 transition-all duration-200",
              "flex flex-col items-center text-center cursor-pointer",
              className
            )}
          >
            <div className="relative w-full h-full">
              {<Image src={poster || unusual_cover} className="w-full h-full rounded-sm object-cover" />}
            </div>
            <div className={cn(
              "absolute -inset-0.5 rounded-lg flex flex-col items-center justify-center px-1 space-y-2",
              poster && "bg-black/30 backdrop-blur-sm"
            )}>
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white/30 backdrop-filter backdrop-blur-lg">
                <WarningRedIcon className="w-4 h-4 text-white" />
              </div>
              <span className="text-white text-xs font-medium underline">
                {t("assets.video_card.unusual_message")}
              </span>
            </div>
            <div
              className="opacity-0 group-hover:opacity-100 absolute top-2 right-2 transition-all"
              onClick={(e) => e.stopPropagation()}
            >
              <DeleteConfirmModal
                trigger={
                  <IconButton
                    className="hover:bg-white/20"
                    icon={<TrashIcon className="w-4 h-4 text-white" />}
                  />
                }
                onConfirm={() => onDelete()}
              />
            </div>
          </div>
          <div className="absolute -bottom-3 w-full text-start translate-y-full">
            <TruncatedText
              text={name}
              className="text-[1rem] font-medium mb-0.5 text-[#101828]"
            />
            <p className="text-[#667085] text-[0.75rem]">
              {formatDateSmart(createdAt).replace(
                "Today",
                t("creation_folder.project_card.today")
              )}
            </p>
          </div>
        </div>
      </a>
    );
  }
  return (
    <div className="w-full h-full relative">
      <div
        className={cn(
          "group relative bg-card border border-border rounded-2xl w-full aspect-[9/16] overflow-hidden",
          "hover:shadow-md hover:border-primary/20 transition-all duration-200",
          "flex flex-col items-center text-center",
          onClick && "cursor-pointer",
          className
        )}
        onClick={onClick}
      >
        {/* 视频容器 */}
        <div className="relative w-full h-full">
          <VideoPlayer
            src={url}
            className="w-full h-full rounded-sm"
            poster={poster}
            showControlsBar={false}
          />

          {/* 生成中状态覆盖层 */}
          {isProcessing && (
            <div className="absolute inset-0 bg-black/40">
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <Spinner className="w-6 h-6 text-white" />
                <p className="px-1 text-white font-medium text-xs">
                  {actor.generateType === 2
                    ? t("assets.video_card.processing_message_image")
                    : t("assets.video_card.processing_message_video")}
                </p>
              </div>
            </div>
          )}

          {/* 失败状态覆盖层 */}
          {isFailed && !isNotFaceDetected && (
            <div
              className="absolute inset-0 bg-black/60 rounded-lg flex flex-col items-center justify-center"
              onClick={handleRegenerateClick}
            >
              {/* 重新生成按钮 */}
              <button className="flex flex-col items-center justify-center px-1 gap-2">
                <RefreshIcon className="w-8 h-8" />
                <span className="text-i-white text-xs font-medium">
                  {t("assets.video_card.failed_message")}
                </span>
              </button>
            </div>
          )}

          {/* 检测不到人脸 状态覆盖层 */}
          {isNotFaceDetected && <ValidMask figureMessage={actor.figureMessage} generateType={actor.generateType} />}

          <div
            className="opacity-0 group-hover:opacity-100 absolute top-2 right-2"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreActionPopover
              className="w-40"
              side="bottom"
              tiggerIcon={
                <MoreIcon className="w-5 h-5 text-white hover:text-white" />
              }
              tiggerClassName="hover:bg-[rgba(0,0,0,0.2)]"
            >
              <RenameModal
                currentName={name}
                trigger={
                  <MoreActionItem
                    icon={<RenameIcon className="w-4 h-4" />}
                    label={t("common.rename")}
                  />
                }
                onConfirm={(name) => onRename(name)}
              />
              <MoreActionItem
                icon={<DownloadIcon className="w-4 h-4" />}
                label={t("common.download")}
                disabled={isProcessing || isFailed}
                onClick={() => onDownload()}
              />
              <DeleteConfirmModal
                trigger={
                  <MoreActionItem
                    icon={<TrashIcon className="w-4 h-4" />}
                    label={t("common.delete")}
                    disabled={isProcessing}
                  />
                }
                onConfirm={() => onDelete()}
              />
            </MoreActionPopover>
          </div>
          {/* 按钮 */}
          {showApply && isSuccess && (
            <ChooseProjectModal
              actor={actor}
              trigger={
                <TextButton
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  icon={<GenerateIcon className="w-5 h-5" />}
                  label={t("assets.apply")}
                  className={cn(
                    "opacity-0 group-hover:opacity-100 absolute bottom-3 left-1/2 -translate-x-1/2",
                    " flex items-center text-sm !transition-all duration-200",
                    "w-[85%] h-8 bg-[#101828] rounded-md text-sm !gap-1.5"
                  )}
                />
              }
            />
          )}
        </div>
      </div>
      <div className="absolute -bottom-3 w-full text-start translate-y-full">
        <TruncatedText text={name} className="text-[1rem] font-medium mb-0.5 text-[#101828]" />
        <p className="text-[#667085] text-[0.75rem]">
          {formatDateSmart(createdAt).replace(
            "Today",
            t("creation_folder.project_card.today")
          )}
        </p>
      </div>
    </div>
  );
};
